import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { InvoiceConceptService } from './invoice-concept.service';
import { InvoiceConcept, UpsertInvoiceConceptRequest } from './invoice-concept.types';
import { InvoicesService } from '../../invoices/invoices.service';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';

interface InvoiceConceptFormValue {
  code: string;
  name: string;
}

type InvoiceConceptFormControlName = keyof InvoiceConceptFormValue;

@Component({
  selector: 'app-invoice-concept',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './invoice-concept.html',
  styleUrl: './invoice-concept.css',
})
export class InvoiceConceptComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly codeMaxLength = 30;
  private readonly nameMaxLength = 100;
  private readonly datatableId = 'kt_invoice_concept_datatable';
  private readonly createModalId = 'create_invoice_concept_modal';
  private readonly editModalId = 'edit_invoice_concept_modal';
  private readonly deleteConfirmModalId = 'delete_invoice_concept_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createInvoiceConceptForm = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(this.codeMaxLength)]],
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
  });
  protected readonly editInvoiceConceptForm = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(this.codeMaxLength)]],
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly invoiceConcepts = signal<InvoiceConcept[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingInvoiceConceptId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingInvoiceConceptId = signal<number | null>(null);
  protected readonly pendingDeleteInvoiceConceptId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(
    private readonly invoiceConceptService: InvoiceConceptService,
    private readonly invoicesService: InvoicesService,
  ) {}

  ngOnInit(): void {
    this.loadInvoiceConcepts();
  }

  ngAfterViewInit(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateInvoiceConceptForm();
    });
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditInvoiceConceptForm();
    });
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: InvoiceConceptFormControlName): boolean {
    return this.createFormSubmitted() && this.createInvoiceConceptForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: InvoiceConceptFormControlName): boolean {
    return this.editFormSubmitted() && this.editInvoiceConceptForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: InvoiceConceptFormControlName): boolean {
    const control = this.createInvoiceConceptForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: InvoiceConceptFormControlName): boolean {
    const control = this.createInvoiceConceptForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: InvoiceConceptFormControlName): boolean {
    const control = this.editInvoiceConceptForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: InvoiceConceptFormControlName): boolean {
    const control = this.editInvoiceConceptForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected loadInvoiceConcepts(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.invoiceConceptService.getInvoiceConcepts().subscribe({
      next: (rows) => {
        this.invoiceConcepts.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load invoice concept data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreateInvoiceConcept(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createInvoiceConceptForm.invalid) {
      this.createInvoiceConceptForm.markAllAsTouched();
      return;
    }

    const payload = this.toPayload(this.createInvoiceConceptForm.getRawValue());

    this.isCreating.set(true);

    this.invoiceConceptService.createInvoiceConcept(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.modalController.close(this.createModalId);
        this.resetCreateInvoiceConceptForm();
        this.invoicesService.invalidateInvoiceConceptOptions();
        window.KTToast?.show({
          message: 'Invoice concept created successfully.',
          variant: 'success',
        });
        this.loadInvoiceConcepts();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create invoice concept at this time. Please try again.');
      },
    });
  }

  protected resetCreateInvoiceConceptForm(): void {
    this.createInvoiceConceptForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditInvoiceConceptModal(invoiceConcept: InvoiceConcept): void {
    this.editingInvoiceConceptId.set(invoiceConcept.id);
    this.editInvoiceConceptForm.reset({
      code: invoiceConcept.code,
      name: invoiceConcept.name,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdateInvoiceConcept(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editInvoiceConceptForm.invalid) {
      this.editInvoiceConceptForm.markAllAsTouched();
      return;
    }

    const invoiceConceptId = this.editingInvoiceConceptId();
    if (invoiceConceptId === null) {
      this.editFormError.set('Unable to identify the invoice concept to update.');
      return;
    }

    const payload = this.toPayload(this.editInvoiceConceptForm.getRawValue());
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.invoiceConceptService.updateInvoiceConcept(invoiceConceptId, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.modalController.close(this.editModalId);
        this.resetEditInvoiceConceptForm();
        this.invoicesService.invalidateInvoiceConceptOptions();
        window.KTToast?.show({
          message: 'Invoice concept updated successfully.',
          variant: 'success',
        });
        this.loadInvoiceConcepts(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update invoice concept at this time. Please try again.');
      },
    });
  }

  protected resetEditInvoiceConceptForm(): void {
    this.editInvoiceConceptForm.reset(this.getEmptyFormValue());
    this.editingInvoiceConceptId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-invoice-concept"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-invoice-concept"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const invoiceConceptId = this.getInvoiceConceptIdFromButton(editButton);
      if (invoiceConceptId === null) {
        return;
      }

      const invoiceConcept = this.invoiceConcepts().find((item) => item.id === invoiceConceptId);
      if (!invoiceConcept) {
        return;
      }

      this.openEditInvoiceConceptModal(invoiceConcept);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const invoiceConceptId = this.getInvoiceConceptIdFromButton(deleteButton);
    if (invoiceConceptId === null) {
      return;
    }

    this.pendingDeleteInvoiceConceptId.set(invoiceConceptId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  protected cancelDeleteInvoiceConcept(): void {
    this.pendingDeleteInvoiceConceptId.set(null);
    this.modalController.close(this.deleteConfirmModalId);
  }

  protected confirmDeleteInvoiceConcept(): void {
    const invoiceConceptId = this.pendingDeleteInvoiceConceptId();

    if (invoiceConceptId === null || this.deletingInvoiceConceptId() !== null) {
      return;
    }

    this.modalController.close(this.deleteConfirmModalId);
    this.pendingDeleteInvoiceConceptId.set(null);
    this.removeInvoiceConcept(invoiceConceptId);
  }

  protected removeInvoiceConcept(invoiceConceptId: number): void {
    if (this.deletingInvoiceConceptId() !== null) {
      return;
    }

    this.deletingInvoiceConceptId.set(invoiceConceptId);

    this.invoiceConceptService.deleteInvoiceConcept(invoiceConceptId).subscribe({
      next: (result) => {
        this.deletingInvoiceConceptId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.invoiceConcepts.update((rows) => rows.filter((item) => item.id !== invoiceConceptId));
          this.invoicesService.invalidateInvoiceConceptOptions();
          this.showSuccessAlert('Invoice concept deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert('Unable to delete invoice concept.', result.message);
      },
      error: () => {
        this.deletingInvoiceConceptId.set(null);
        this.showDestructiveAlert(
          'Unable to delete invoice concept.',
          'Unable to delete invoice concept at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getInvoiceConceptIdFromButton(button: HTMLButtonElement): number | null {
    const rawId = button.getAttribute('data-invoice-concept-id');
    const parsed = rawId ? Number(rawId) : Number.NaN;

    return Number.isFinite(parsed) ? parsed : null;
  }

  private getEmptyFormValue(): InvoiceConceptFormValue {
    return {
      code: '',
      name: '',
    };
  }

  private toPayload(rawValue: InvoiceConceptFormValue): UpsertInvoiceConceptRequest {
    return {
      code: rawValue.code.trim().toUpperCase(),
      name: rawValue.name.trim(),
    };
  }
}
